import { API_HOST, CLIENT_ID, CLIENT_SECRET } from '@/src/app.config'
import axios from 'axios'

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  accessToken: getSavedState('auth.accessToken'),
  reAuthAttempts: getSavedState('auth.reAuthAttempts'),
  loginEpoch: parseInt(decode(getSavedState('auth.loginEpoch'))),
  deAuthed: getSavedState('auth.deAuthed'),
  isRevalidating: false,
  hashedUserName: 0,
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    if (newValue) {
      newValue.lastUpdate = new Date().getTime()
    }
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
  },
  SET_ACCESS_TOKEN(state, newValue) {
    state.accessToken = newValue
    saveState('auth.accessToken', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_REAUTH_ATTEMPTS(state, newValue) {
    state.reAuthAttempts = newValue
    saveState('auth.reAuthAttempts', newValue)
  },
  SET_LOGIN_EPOCH(state, newValue) {
    state.loginEpoch = newValue
    saveState('auth.loginEpoch', encode(newValue))
  },
  SET_REVALIDATING(state, newValue) {
    state.isRevalidating = newValue
  },
  SET_DE_AUTHED(state, newValue) {
    state.deAuthed = newValue
  },
  SET_HASHED_USER(state, newValue) {
    state.hashedUserName = Math.abs([...newValue].reduce((accum, val, i) => accum = (((accum << 5) - accum) +  newValue.charCodeAt(i)) | 0), 0)
  },
}

export const getters = {
  isDeAuthed(state) {
    return state.deAuthed
  },
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.accessToken
  },
  getUserPermissions(state) {
    if (state.currentUser && state.currentUser.permissions) {
      return state.currentUser.permissions
    }
    return null
  },
  getUserType(state) {
    if (state.currentUser && state.currentUser.login_as) {
      return state.currentUser.login_as
    }
    return null
  },
  isXeroConnected(state) {
    if (
      state.currentUser &&
      state.currentUser.business &&
      state.currentUser.business.xero_account
    ) {
      return state.currentUser.business.xero_account
    }
    return null
  },
}

export const actions = {
  // De-auth the user as they have been logged out via some external mechanism
  // if we are currently re-validating ignore
  deAuthUser({ commit, state }) {
    if (!state.isRevalidating) {
      commit('SET_DE_AUTHED', true)
      console.log("forced logout")
    }
  },

  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  async init({ commit, state, dispatch }) {
    setDefaultAuthHeaders(state)
    commit('SET_DE_AUTHED', false)
    await dispatch('validate')
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { username, password } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    const grantType = 'password'
    const clientSecret = CLIENT_SECRET
    const clientId = CLIENT_ID

    return axios
      .post('/oauth/token', {
        grant_type: grantType,
        client_secret: clientSecret,
        client_id: clientId,
        username,
        password,
      })
      .then((response) => {
        const accessToken = response.data
        commit('SET_ACCESS_TOKEN', accessToken)
        commit('SET_REAUTH_ATTEMPTS', 0)
        commit('SET_LOGIN_EPOCH', Date.now())
        return dispatch('validate')
      })
  },

  // Logs out the current user.
  logOut({ commit, dispatch, state }) {
    try {
      axios.post('/oauth/logout')
    } catch (exception) {
      console.warn(
        `Unable to resolve logout response from server: ${exception}`
      )
      const currentTime = Date.now()
      if (currentTime < state.currentUser.token_refresh_expires) {
        const clientSecret = CLIENT_SECRET
        const clientId = CLIENT_ID
        return axios
          .post('/oauth/token', {
            grant_type: 'refresh_token',
            refresh_token: state.accessToken.refresh_token,
            client_secret: clientSecret,
            client_id: clientId,
          })
          .then((response) => {
            const accessToken = response.data
            commit('SET_ACCESS_TOKEN', accessToken)
            commit('SET_REAUTH_ATTEMPTS', state.reAuthAttempts + 1)
            // re-dispatch as we have a updated token
            return dispatch('logOut')
          })
      } else {
        self.router.push({ name: 'login' })
      }
    }
    commit('SET_CURRENT_USER', null)
    commit('SET_ACCESS_TOKEN', null)
    commit('SET_REAUTH_ATTEMPTS', null)
    commit('SET_LOGIN_EPOCH', null)
    commit('SET_DE_AUTHED', false)
  },

  outlookLogin() {
    return axios.get('/oauth/outlook_login')
  },

  outlookAuthorize({ commit, dispatch, getters }, code) {
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/oauth/outlook_login', {
        code,
      })
      .then((response) => {
        const accessToken = response.data
        commit('SET_ACCESS_TOKEN', accessToken)
        commit('SET_REAUTH_ATTEMPTS', 0)
        return dispatch('validate')
      })
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  // this should be SYNCHRONOUS, but anything dispatched is a promise
  // thus wrap as async and await all methods
  async validate({ commit, dispatch, state }) {
    if (state.isRevalidating) {
      console.warn(
        'Ignoring additional call made to validate while currently revalidating'
      )
      return true
    }
    if (!state.accessToken) return Promise.resolve(null)
    try {
      const { data } = await axios.get('/me')
      const user = data.data
      commit('SET_CURRENT_USER', user)
      commit('SET_HASHED_USER', user?.email || 'no email provided')
      return user
    } catch (exception) {
      console.warn('Validate exception: ', exception)
      return await refreshToken(commit, dispatch, state)
    }
  },
}

// ===
// Private helpers
// ===

async function refreshToken(commit, dispatch, state) {
  if (state.isRevalidating) {
    console.warn(
      'Ignoring additional call made to revalidate while currently revalidating'
    )
    return
  }
  commit('SET_REVALIDATING', true)
  const currentTime = Date.now()
  if (
    state.currentUser &&
    state.currentUser.token_refresh_expires &&
    currentTime < state.currentUser.token_refresh_expires &&
    state.reAuthAttempts < 6
  ) {
    const clientSecret = CLIENT_SECRET
    const clientId = CLIENT_ID
    try {
      const { data } = await axios.post('/oauth/token', {
        grant_type: 'refresh_token',
        refresh_token: state.accessToken.refresh_token,
        client_secret: clientSecret,
        client_id: clientId,
      })
      commit('SET_ACCESS_TOKEN', data)
      commit('SET_REAUTH_ATTEMPTS', state.reAuthAttempts + 1)
      // re-dispatch as we have a updated token
      commit('SET_REVALIDATING', false)
      commit('SET_DE_AUTHED', false)
      return dispatch('validate')
    } catch (exception) {
      commit('SET_CURRENT_USER', null)
      commit('SET_ACCESS_TOKEN', null)
      commit('SET_REAUTH_ATTEMPTS', null)
      commit('SET_LOGIN_EPOCH', null)
      commit('SET_REVALIDATING', false)
      commit('SET_DE_AUTHED', true)
      return false
    }
  }
  commit('SET_CURRENT_USER', null)
  commit('SET_ACCESS_TOKEN', null)
  commit('SET_REAUTH_ATTEMPTS', null)
  commit('SET_LOGIN_EPOCH', null)
  commit('SET_REVALIDATING', false)
  commit('SET_DE_AUTHED', true)
  return false
}

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  axios.defaults.baseURL = API_HOST
  axios.defaults.headers.common.Authorization = state.accessToken
    ? state.accessToken.token_type + ' ' + state.accessToken.access_token
    : ''
}

function encode(value) {
  return window.btoa(value)
}

function decode(value) {
  return window.atob(value)
}
